import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { ElementRef, Injectable } from '@angular/core';
import { Capacitor, CapacitorHttp, HttpOptions } from '@capacitor/core';
import { loadStripe } from '@stripe/stripe-js';
import { lastValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import * as html2pdf from 'html2pdf.js';
import { BaseHttpService } from '../auth/base-capacitor-http';
import { Preferences } from '@capacitor/preferences';
import { AuthResponse } from '../auth/auth.service';
import { MatSnackBar } from '@angular/material/snack-bar';

export interface Receipt {

  email: string;
  ref: string;
  startDate: Date;
  price: number;
  name: string;
  image: string;
  summary: OrderSummary;

}


export class OrderSummary {
  discount: number;
  total: number;
  subTotal: number;
  shipping: number;
  tax: number;
}


export interface OrderItem {
  quantity: number;
  shipped: number;
  product: ProductDisplay;

}

export interface SubData {
  active: boolean;
  expires: number;

}

export interface Subscription {
  id: string;
  date: Date;
  status: string;
  paymentMethod: string;
  items: SubItem[];
  total: number;
}

export interface SubItem {
  name: string;
  quantity: number;
  price: number;
}
export interface CheckoutSession {
  id: string;
  sid?: string;
  line_items: CheckoutLineItem[],
  mode: 'payment',
  success_url: string,
  cancel_url: string
}

export interface CheckoutLineItem {
  currency: 'usd',
  price_id: string;
  description: string;
  name: string,
  images: string[]
  unit_amount: number,
  quantity: number,
}

export interface ProductDisplay {
  name: string;
  description: string;
  url: string;
  amount: number;
  interval: string;
  currency: string;
  type: string;
  price_id: string;
  images: string[];
  features: string[];
}



@Injectable({
  providedIn: 'root'
})
export class PaymentService extends BaseHttpService {

  private baseUrl: string;
  private urlEndPoint: string;

  constructor(client: HttpClient, snackBar: MatSnackBar) {
    super(client, snackBar);
    this.baseUrl = environment.server.mainURL;
  }

  private async getStripePk(): Promise<{ pk: string }> {
    this.urlEndPoint = this.baseUrl + 'finance/pk';
    return this.get<{ pk: string }>(this.urlEndPoint);
  }


  async getCheckoutSession(product: ProductDisplay) {

    this.urlEndPoint = this.baseUrl + 'finance/checkout';
    let body = {
      url: product?.url ?? '',
      name: product.name,
      success_url: environment.server.domain + 'success?session_id={CHECKOUT_SESSION_ID}',// as per documentation
      cancel_url: environment.server.domain + 'cart-view',
      price_id: product.price_id,
      coupon_id: ''
    }

    return this.post<CheckoutSession>(this.urlEndPoint, body);
  }

  async checkout(sessionId: string) {
    const pk: string = (await this.getStripePk()).pk;
    const stripe = await loadStripe(pk);

    return stripe.redirectToCheckout({ sessionId: sessionId });
  }

  async openCustomerPortal() {
    const res = await this.customerPortal();
    window.location.href = res.url;
  }

  private async customerPortal() {
    this.urlEndPoint = this.baseUrl + 'finance/billing-portal';
    let body = {
      returnUrl: environment.server.domain + 'settings'
    };

    return this.post<{ url: string }>(this.urlEndPoint, body);
  }


  downLoadReceipt(element: ElementRef) {

    const options = {
      margin: 10,
      filename: 'receipt.pdf',
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: {
        scale: 2,
        useCORS: true, // Important if you have images or fonts from external sources
      },
      jsPDF: {
        unit: 'mm',
        format: 'a4',
        orientation: 'portrait',
      }
    };

    // Ensure the element exists
    if (!element?.nativeElement) {
      console.error('ElementRef is invalid or not found');
      return;
    }

    html2pdf()
      .set(options)
      .from(element.nativeElement)
      .save()
      .catch(err => {
        console.error('PDF generation error:', err);
      });
  }

  async emailPdf(element: ElementRef) {
    const options = {
      margin: 10,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
    };

    const worker = html2pdf()
      .set(options)
      .from(element.nativeElement);

    const blob: Blob = await worker.outputPdf('blob');
    const formData = new FormData();
    formData.append('file', blob, 'receipt.pdf');

    this.urlEndPoint = this.baseUrl + 'finance/email-receipt'
    const headers = await this.getMediaAuthHeaders();
    if (Capacitor.isNativePlatform()) {
      const httpOptions: HttpOptions = { url: this.urlEndPoint, data: formData };
      httpOptions.headers = headers;
      httpOptions.webFetchExtra.credentials = 'include';

      const res = await CapacitorHttp.post(httpOptions);
      return res.data as boolean;
    }
    const httpOptions = {
      headers: new HttpHeaders(headers),
      withCredentials: true
    }

    return lastValueFrom(this.client.post<boolean>(this.urlEndPoint, formData, httpOptions));

  }

  async getUserPayHistory() {
    this.urlEndPoint = this.baseUrl + 'finance/receipt';
    return this.get<Subscription>(this.urlEndPoint);
  }

  async getReceipt(sessionId: string) {
    this.urlEndPoint = this.baseUrl + 'finance/session-receipt';
    return this.get<Receipt>(this.urlEndPoint, { session_id: sessionId });
  }

  async checkForActiveSubscription() {
    this.urlEndPoint = this.baseUrl + 'finance/check-subscription';
    return this.get<AuthResponse>(this.urlEndPoint);
  }

  async getProducts(): Promise<ProductDisplay[]> {
    this.urlEndPoint = this.baseUrl + 'commons/product-info';
    return this.get<ProductDisplay[]>(this.urlEndPoint);
  }

  async storeSubInfo(subData: SubData) {
    let response: string = this.encryptPayLoad<SubData>(subData, this.KEY);
    await Preferences.set({ key: 'sub', value: response });
  }

  async getSubInfo() {
    let { value } = await Preferences.get({ key: 'sub' });
    if (value) {
      let response = this.decryptPayload<SubData>(value, this.KEY);
      return response;
    }
    return null;
  }

  async cancelCustomerActiveSubscription(subId: string, type: string) {
    this.urlEndPoint = this.baseUrl + 'finance/subscription/' + subId;


    let body = {
      type: type,
    };

    return this.put<Subscription>(this.urlEndPoint, body);
  }

  async getCustomerActiveSubscription(): Promise<ProductDisplay[]> {
    this.urlEndPoint = this.baseUrl + 'finance/subscription-all';
    return this.get<ProductDisplay[]>(this.urlEndPoint);
  }


}
